import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, Settings, Calendar, Package, Trash2, Play, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAutoUpdate = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  
  // Config state
  const [enabled, setEnabled] = useState(false);
  const [intervalDays, setIntervalDays] = useState("3");
  const [productsPerRun, setProductsPerRun] = useState("100");

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/catalog/auto-update-status`);
      setStatus(response.data);
      setEnabled(response.data.enabled);
      setIntervalDays(String(response.data.interval_days));
      setProductsPerRun(String(response.data.products_per_run));
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      await axios.post(`${API}/admin/catalog/auto-update-config?enabled=${enabled}&interval_days=${intervalDays}&products_per_run=${productsPerRun}`);
      toast.success('Configurazione salvata!');
      fetchStatus();
    } catch (error) {
      toast.error('Errore nel salvare la configurazione');
    }
  };

  const runUpdate = async () => {
    setUpdating(true);
    try {
      await axios.post(`${API}/admin/catalog/run-auto-update`);
      toast.success('Aggiornamento avviato in background!');
      // Poll for completion
      setTimeout(fetchStatus, 5000);
      setTimeout(fetchStatus, 15000);
      setTimeout(fetchStatus, 30000);
    } catch (error) {
      toast.error('Errore nell\'avvio dell\'aggiornamento');
    } finally {
      setTimeout(() => setUpdating(false), 30000);
    }
  };

  const runCleanup = async () => {
    setCleaning(true);
    try {
      const response = await axios.post(`${API}/admin/catalog/cleanup-old?days_old=60&keep_min=1500`);
      toast.success(`Pulizia completata! ${response.data.removed} prodotti rimossi`);
      fetchStatus();
    } catch (error) {
      toast.error('Errore nella pulizia');
    } finally {
      setCleaning(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'Mai';
    const date = new Date(isoString);
    return date.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-neutral-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-auto-update">
      <div>
        <h1 className="text-3xl font-light tracking-tight">Aggiornamento Automatico</h1>
        <p className="text-neutral-500 mt-1">Configura l'aggiornamento automatico del catalogo</p>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-neutral-200 p-6">
          <div className="flex items-center gap-3 mb-2">
            {status?.enabled ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <AlertCircle className="h-5 w-5 text-neutral-400" />
            )}
            <span className="text-sm text-neutral-500">Stato</span>
          </div>
          <p className="text-lg font-medium">{status?.enabled ? 'Attivo' : 'Disattivato'}</p>
        </div>
        
        <div className="bg-white border border-neutral-200 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="h-5 w-5 text-neutral-400" />
            <span className="text-sm text-neutral-500">Ultimo Aggiornamento</span>
          </div>
          <p className="text-lg font-medium">{formatDate(status?.last_run)}</p>
        </div>
        
        <div className="bg-white border border-neutral-200 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Package className="h-5 w-5 text-neutral-400" />
            <span className="text-sm text-neutral-500">Ultimi Importati</span>
          </div>
          <p className="text-lg font-medium">{status?.last_imported || 0} prodotti</p>
        </div>
        
        <div className="bg-white border border-neutral-200 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="h-5 w-5 text-neutral-400" />
            <span className="text-sm text-neutral-500">Prossimo Aggiornamento</span>
          </div>
          <p className="text-lg font-medium">{formatDate(status?.next_run)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Configuration */}
        <div className="bg-white border border-neutral-200 p-6">
          <h2 className="text-xl font-medium mb-6 flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configurazione
          </h2>

          <div className="space-y-6">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Aggiornamento Automatico</Label>
                <p className="text-sm text-neutral-500 mt-1">
                  Importa automaticamente nuovi prodotti da CJ
                </p>
              </div>
              <Switch 
                checked={enabled} 
                onCheckedChange={setEnabled}
                data-testid="toggle-auto-update"
              />
            </div>

            {/* Interval */}
            <div className="space-y-2">
              <Label>Intervallo (giorni)</Label>
              <Select value={intervalDays} onValueChange={setIntervalDays}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Ogni giorno</SelectItem>
                  <SelectItem value="2">Ogni 2 giorni</SelectItem>
                  <SelectItem value="3">Ogni 3 giorni</SelectItem>
                  <SelectItem value="7">Ogni settimana</SelectItem>
                  <SelectItem value="14">Ogni 2 settimane</SelectItem>
                  <SelectItem value="23">Ogni 23 giorni</SelectItem>
                  <SelectItem value="30">Ogni mese</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Products per run */}
            <div className="space-y-2">
              <Label>Prodotti per aggiornamento</Label>
              <Select value={productsPerRun} onValueChange={setProductsPerRun}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50 prodotti</SelectItem>
                  <SelectItem value="100">100 prodotti</SelectItem>
                  <SelectItem value="200">200 prodotti</SelectItem>
                  <SelectItem value="300">300 prodotti</SelectItem>
                  <SelectItem value="500">500 prodotti</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={saveConfig} className="w-full">
              Salva Configurazione
            </Button>
          </div>
        </div>

        {/* Manual Actions */}
        <div className="bg-white border border-neutral-200 p-6">
          <h2 className="text-xl font-medium mb-6 flex items-center gap-2">
            <Play className="h-5 w-5" />
            Azioni Manuali
          </h2>

          <div className="space-y-4">
            <div className="p-4 bg-neutral-50 rounded-lg">
              <h3 className="font-medium mb-2">Esegui Aggiornamento Ora</h3>
              <p className="text-sm text-neutral-500 mb-4">
                Avvia immediatamente l'importazione di nuovi prodotti da CJDropshipping
              </p>
              <Button 
                onClick={runUpdate} 
                disabled={updating}
                className="w-full"
              >
                {updating ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                    Aggiornamento in corso...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Aggiorna Catalogo
                  </>
                )}
              </Button>
            </div>

            <div className="p-4 bg-neutral-50 rounded-lg">
              <h3 className="font-medium mb-2">Pulizia Catalogo</h3>
              <p className="text-sm text-neutral-500 mb-4">
                Rimuovi prodotti vecchi (più di 60 giorni) mantenendo almeno 1500 prodotti
              </p>
              <Button 
                variant="outline"
                onClick={runCleanup} 
                disabled={cleaning}
                className="w-full"
              >
                {cleaning ? (
                  <>
                    <Trash2 className="h-4 w-4 animate-pulse mr-2" />
                    Pulizia in corso...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Pulisci Prodotti Vecchi
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">Come Funziona</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Il sistema importa nuovi prodotti da CJ automaticamente</li>
              <li>• Usa keyword random per varietà (fashion, luxury, home...)</li>
              <li>• Evita duplicati controllando l'ID prodotto CJ</li>
              <li>• Puoi pulire i prodotti vecchi periodicamente</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAutoUpdate;
